import { useState, useEffect } from "react"
import { BarChart3, TrendingUp, Calendar, Building, Users, FileText } from "lucide-react"
import StatsOverview from "../GMSInsights/StatsOverview"
import VillageMap from "../GMSInsights/VillageMap"
import RecentGrievances from "../GMSInsights/RecentGrievances"
import GrievanceTrend from "../GMSInsights/GrievanceTrend"
import useAxiosInstance from "../../../lib/useAxiosInstance"
import { DatePicker } from "antd"
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"

const { RangePicker } = DatePicker

const GMSInsightsView = () => {
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [dateRange, setDateRange] = useState(null)
  const [duration, setDuration] = useState("Past Week")
  const [data, setData] = useState(null)
  const [durationData, setDurationData] = useState(null)
  const [activeTab, setActiveTab] = useState("insights")
  const [loading, setLoading] = useState(false)
  const axiosInstance = useAxiosInstance()
  const themes = localStorage.getItem("themes")

  useEffect(() => {
    if (dateRange) {
      const [start, end] = dateRange
      setStartDate(start.toISOString())
      setEndDate(end.toISOString())
    }
  }, [dateRange])

  useEffect(() => {
    if (activeTab === "insights") {
      fetchData()
    }
  }, [startDate, endDate, activeTab])

  useEffect(() => {
    if (activeTab === "trends") {
      fetchDurationData()
    }
  }, [startDate, endDate, duration, activeTab])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await axiosInstance.get(
        `/api/grievances/getGrievanceInsights?startDate=${startDate}&endDate=${endDate}&duration=${duration}`
      )
      setData(response?.data)
    } catch (err) {
      console.error("Fetch Insights Error", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchDurationData = async () => {
    setLoading(true)
    try {
      const response = await axiosInstance.get(
        `/api/grievances/getGrievanceDurationCount?startDate=${startDate}&endDate=${endDate}&duration=${duration}`
      )
      setDurationData(response?.data)
    } catch (err) {
      console.error("Fetch Duration Error", err)
    } finally {
      setLoading(false)
    }
  }

  const officeBarData = data?.data.officeWise?.map((office) => ({
    name: office.officeName.length > 15 ? office.officeName.slice(0, 15) + "..." : office.officeName,
    total: office.totalGrievances,
    resolved: office.resolvedGrievances,
    inProgress: office.inProgressGrievances,
  })) || []

  const categoryBarData = data?.data.categoryWise?.map((item) => ({
    name: item.categoryName.split("-")[0],
    total: item.totalGrievances,
    resolved: item.resolvedGrievances,
    inProgress: item.inProgressGrievances,
  })) || []

  const chartData = data?.data.genderWise
    ? [
        {
          name: "Female",
          value: data.data.genderWise.find((g) => g.gender === "female")?.percentage || 0,
          count: data.data.genderWise.find((g) => g.gender === "female")?.count || 0,
          fill: "#bfdbfe",
        },
        {
          name: "Male",
          value: data.data.genderWise.find((g) => g.gender === "male")?.percentage || 0,
          count: data.data.genderWise.find((g) => g.gender === "male")?.count || 0,
          fill: "#1e3a8a",
        },
      ]
    : []

  return (
    <div className="p-6 space-y-6">
      {/* Top Filters & Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-color-heading flex items-center gap-2">
            <BarChart3 className="text-blue-600" />
            Grievance Insights
          </h2>
          <p className="text-sm text-gray-500">Analytics & Reporting</p>
        </div>
        <div className="flex items-center gap-2">
          {/* <Calendar size={16} className="text-gray-500" /> */}
          <RangePicker
            allowClear
            onChange={(dates) => {
              if (!dates) {
                setDateRange(null)
                setStartDate("")
                setEndDate("")
              } else {
                setDateRange(dates)
              }
            }}
            placeholder={["Start Date", "End Date"]}
            size="middle"
          />
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-4 border-b pb-2">
        <button
          onClick={() => setActiveTab("insights")}
          className={`px-4 py-2 font-medium border-b-2 ${
            activeTab === "insights"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-blue-600"
          }`}
        >
          Detailed Insights
        </button>
        <button
          onClick={() => setActiveTab("trends")}
          className={`px-4 py-2 font-medium border-b-2 ${
            activeTab === "trends"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-blue-600"
          }`}
        >
          Trends
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : activeTab === "insights" && data ? (
        <div className="space-y-6">
          {/* <StatsOverview data={data.data.summaryOverview} /> */}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6 h-[70vh]">
              {/* Office Chart & Gender Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[35vh]">
                <div className="lg:col-span-8 rounded-xl border border-gray-200 insights-card">
                  {/* Office Chart */}
                    <div className="col-span-8 rounded-xl p-2">
                        <h3 className="text-sm font-bold text-color mb-3 flex items-center gap-2">
                          <Building size={16} className="text-blue-600" />
                          Office-wise Distribution
                        </h3>
                        <div className="h-[30vh]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={officeBarData} margin={{ top: 5, right: 10, left: 0, bottom: 10 }}>
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                              <YAxis fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="total" fill="#1e3a8a" name="Total" />
                              <Bar dataKey="resolved" fill="#3b82f6" name="Resolved" />
                              <Bar dataKey="inProgress" fill="#bfdbfe" name="In Progress" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-4 insights-card rounded-xl border border-gray-200">
                  {/* Gender Chart */}
                   <div className="col-span-4 rounded-xl p-2">
                        <h3 className="text-sm font-bold text-color mb-0 flex items-center gap-2">
                          <Users size={16} className="text-purple-600" />
                          Gender Distribution
                        </h3>
                        <div className="flex flex justify-center h-[30vh] space-y-4">
                          <div className="flex flex-col items-center gap-2">
                            <RadialBarChart
                              width={250}
                              height={250}
                              cx="50%"
                              cy="50%"
                              innerRadius="70%"
                              outerRadius="100%"
                              barSize={10}
                              data={chartData}
                            >
                              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                              <RadialBar minAngle={5} background clockWise dataKey="value" />
                              {chartData.map((item, index) => (
                                <text
                                  key={index}
                                  x="50%"
                                  y={`${50 + index * 15}%`} // Staggering the labels vertically
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  className="text-sm font-bold"
                                  fill={`${themes!=='darkMode'?'#4B5563':'#fff'}`} // Tailwind gray-700 4B5563
                                >
                                  {item.name}: {item.value}%
                                </text>
                              ))}
                            </RadialBarChart>
                            <div className="flex gap-4">
                              {chartData.map((item) => (
                                <div key={item.name} className="flex justify-center items-center gap-1">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: item.fill }} 
                                    ></div>
                                  <div className="text-sm font-bold text-color capitalize">{item.name}:</div>
                                  <div className={`text-sm ${themes !== 'darkMode' ? 'text-gray-500':'text-gray-200'}`}>{item.count.toLocaleString()}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
              </div>

              {/* Category Chart */}
              <div className="insights-card p-4 rounded-xl border border-gray-200">
                {/* Category-wise Chart */}
                <h3 className="text-sm font-bold text-color mb-3 flex items-center gap-2">
                        <FileText size={16} className="text-green-600" />
                        Category-wise Distribution
                      </h3>
                      <div className="h-[25vh]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={categoryBarData} margin={{ top: 5, right: 30, left: 20, bottom: 40 }}>
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                            <YAxis fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="total" fill="#1e3a8a" name="Total" />
                            <Bar dataKey="resolved" fill="#3b82f6" name="Resolved" />
                            <Bar dataKey="inProgress" fill="#bfdbfe" name="In Progress" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
              </div>
            </div>

            {/* Map */}
            <div className="lg:col-span-4">
              <VillageMap data={data.data.wardWise} />
            </div>
          </div>
        </div>
      ) : activeTab === "trends" && durationData ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GrievanceTrend data={durationData?.data} duration={duration} setDuration={setDuration} themes={themes} />
          <RecentGrievances grievances={data?.data?.grievances || []} />
        </div>
      ) : (
        <div className="text-center text-gray-500 py-12">No data available</div>
      )}
    </div>
  )
}

export default GMSInsightsView
